import { EcommerceAPIApi as EcommerceApi, ReservationContractSignupSaveRequest, CustomerReservationSaveRequest, Reservation, ApplySpecialRateToReservationRequest } from '@parkingbase/ecommerce-api-demo';
import { PAYMENT_TIMEOUT_MS } from '@/lib/api/payment-timeout';
import { BaseApiService } from './baseApi.service';

export class ReservationService extends BaseApiService {
  protected reservationApi: EcommerceApi;
  protected monthlyAccountApi: EcommerceApi;

  constructor() {
    super();
    this.reservationApi = new EcommerceApi(this.apiConfig, undefined, this.apiClient);
    this.monthlyAccountApi = new EcommerceApi(this.apiConfig, undefined, this.apiClient);
  }

  async getReservationsByUser() {
    const response = await this.reservationApi.getReservationsByUser();
    return response.data;
  }

  async getPendingDisclaimertasks() {
    const response = await this.reservationApi.getPendingDisclaimertasks();
    return response.data;
  }

  async cancelReservation(reservationId: string, wholeGroup?: boolean) {
    // PB-2947 Independent: wholeGroup=false cancels ONLY this window of an
    // independent-booking purchase (no refund). `wholeGroup` is now a first-class
    // client parameter (the api-dev client regenerated with it).
    const response =
      wholeGroup === undefined
        ? await this.reservationApi.cancelCustomerReservation(reservationId)
        : await this.reservationApi.cancelCustomerReservation(reservationId, wholeGroup);
    return response.data;
  }

  async downloadReceipt(company: string, reservationId: string) {
    const response = await this.reservationApi.getReservationReceipt(company, reservationId, {
      responseType: 'blob',
    });
    return response.data;
  }

  async getTaskDetails(companyId: string, taskId: string) {
    const response = await this.reservationApi.monthlyAccountDetailsUsingTask1(
      companyId,
      taskId
    );
    return response.data;
  }

  async getDisclaimerContract(
    company: string,
    garage: string,
    disclaimerType: string,
    accountId?: string
  ) {
    const response = await this.monthlyAccountApi.getDisclaimerContract(
      company,
      garage,
      disclaimerType,
      accountId
    );
    return response.data;
  }

  async getDisclaimerContractForPermitRate(
    company: string,
    garage: string,
    disclaimerType: string,
    templateId: string
  ) {
    const response = await this.monthlyAccountApi.getDisclaimerContractForPermitRate(
      company,
      garage,
      disclaimerType,
      templateId
    );
    return response.data;
  }

  async signContract(
    companyId: string,
    taskId: string,
    payload: ReservationContractSignupSaveRequest
  ) {
    const response = await this.reservationApi.updateMonthlyAccountafterContract(
      companyId,
      taskId,
      payload
    );
    return response.data;
  }

  async getRecentZoneTickets() {
    const response = await this.reservationApi.getRecentZoneTickets();
    return response.data;
  }

  async lookupSpecialRate(request: ApplySpecialRateToReservationRequest) {
    const response = await this.reservationApi.getSpecialRateDetailsForReservation(request);
    return response.data;
  }

  async addCustomerReservation(request: CustomerReservationSaveRequest): Promise<Reservation> {
    // PB-2947: a recurring-series purchase fans out into one reservation per
    // occurrence block server-side, and each block does its own receipt/email
    // work — the response can legitimately exceed the default 30s client
    // timeout. Timing out mid-fan-out is worse than waiting: the payment is
    // already authorized, so a "failed" modal invites a double-purchase retry.
    const response = await this.reservationApi.addCustomerReservation(request, {
      timeout: PAYMENT_TIMEOUT_MS,
    });
    return response.data;
  }

  async getOpenTicketDetailsFromWhiteLabel() {
    const response = await this.reservationApi.getOpenTicketDetailsFromWhiteLabel();
    return response.data;
  }
}
